import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { FormField, FormFields } from "../../../components/FormFields";
import { VBox } from "../../../components/Box";
import { FieldError } from "../../../components/FieldError";
import { Input } from "../../../components/Input";
import { getBpmEstimate, setVehicleProperty } from "../actions";
import { ConfirmBar } from "../components/ConfirmBar";
import { BaseComponent } from "../../../components/BaseComponent";
import { Radio } from "../../../components/Radio";
import { PRICE_TYPES } from "../../Tools/constants";
import { BpmEstimate } from "../components/BpmEstimate";
import { selectWorks } from "../../Api/selectors";
import {REQUEST_VEHICLE_BPM_ESTIMATE, REQUEST_VEHICLE_PROPERTY_UPDATE} from "../constants";
import { Loader } from "../../../components/Loader";

const Container = styled.div``;

const STATE = {
    priceType: null,
    bidAmount: null,
    restBPM: null,
    b2bPrice: null,
    b2cPrice: null,
    fieldErrors: null,
    bpmEstimate: null,
}

/* eslint-disable react/prefer-stateless-function */
class VehicleDetailsPriceComp extends BaseComponent {
    constructor(props) {
        super(props, {...STATE});
        const vehicleProps = [
            'priceType',
            'bidAmount',
            'restBPM',
            'b2bPrice',
            'b2cPrice',
        ].reduce((acc, name) => {
            acc[name] = this.getVehicleProperty(name);
            return acc;
        }, {});
        this.state = {...STATE, ...vehicleProps};
    }

    componentDidMount() {
        this.calculateBpmEstimate();
    }

    calculateBpmEstimate() {
        const vehicleId = this.entityId;
        this.props.calculateBpmEstimate(
            vehicleId,
            data => {
                let errors = null;
                if (data.errors && Array.isArray(data.errors)) {
                    errors = data.errors.reduce((prev, curr) => {
                        let firstError = Object.values(curr.constraints)[0];
                        prev.push(firstError);
                        return prev;
                    }, []);
                }
                let result = data.result ?? null;
                this.setState(prev => {
                    return {
                        ...prev,
                        bpmEstimate: {result, errors},
                    };
                });
            })
    }

    onInputChange(evt) {
        const name = evt.target.name
        const value = evt.target.value === "" ? STATE[name]: evt.target.value;
        this.updateState(name, value);
    }

    get vehicle() {
        return this.props.vehicle;
    }

    get entityId() {
        return this.vehicle?.id;
    }

    getVehicleProperty(name) {

        return this.props.vehicle.properties.find(p => p.propertyName.name === name)?.propertyValue?.value ?? null;
    }

    isDirty(name) {
        const oldValue = this.getVehicleProperty(name);
        return oldValue === this.state[name];
    }

    get priceType() {
        return this.getVehicleProperty('priceType');
    }

    renderConfirmBar(name) {
        return (
            this.isDirty(name) || <ConfirmBar apply={evt => this.setVehicleProperty(evt, name)} cancel={evt => this.resetField(evt, name)} />
        );
    }

    setVehicleProperty(evt, name) {
        const vehicleId = this.props.vehicle.id;
        const value = this.state[name];
        this.props.setVehicleProperty(vehicleId, name, value);
    }

    resetField(evt, name) {
        this.setState(prev => {
            return {
                ...prev,
                [name]: this.getVehicleProperty(name),
            };
        });
    }

    applyBpmEstimate(amount) {
        this.updateState('restBPM', amount);
    }

    get bidAmount() {
        return this.state.bidAmount;
    }

    get bidAmountErrors() {
        return this.state.fieldErrors?.b2cPrice;
    }

    get restBPM() {
        return this.state.restBPM;
    }

    get restBPMErrors() {
        return this.state.fieldErrors?.b2bPrice;
    }

    get b2bPrice() {
        return this.state.b2bPrice;
    }

    get b2bPriceErrors() {
        return this.state.fieldErrors?.b2cPrice;
    }

    get b2cPrice() {
        return this.state.b2cPrice;
    }

    get b2cPriceErrors() {
        return this.state.fieldErrors?.b2cPrice;
    }

    get bpmEstimate() {
        return this.state.bpmEstimate;
    }

    render() {
        if (this.props.propertyUpdateWork) return <Loader small />;

        return (
            <Container>
                <FormFields>
                    <VBox vCenter>
                        <p>{this.lbl('vehicle.details.price.priceType.formLbl')}</p>
                        <Radio
                            checked={this.state['priceType'] === PRICE_TYPES.BTW}
                            onClick={this.setStateFn({ ['priceType']: PRICE_TYPES.BTW })}
                        >
                            {this.lbl('vehicle.details.price.priceType.btwOption')}
                        </Radio>
                        <Radio
                            checked={this.state['priceType'] === PRICE_TYPES.MARGE}
                            onClick={this.setStateFn({ ['priceType']: PRICE_TYPES.MARGE })}
                        >
                            {this.lbl('vehicle.details.price.priceType.margeOption')}
                        </Radio>
                        {this.renderConfirmBar('priceType')}
                    </VBox>
                    <VBox>
                        <p>{this.lbl('vehicle.details.price.bidAmount.inputLbl')}</p>
                        <FormField>
                            <Input value={this.bidAmount ?? ""} name="bidAmount" onChange={this.onInputChange.bind(this)} />
                            {this.renderConfirmBar('bidAmount')}
                            <FieldError errors={this.bidAmountErrors} />
                        </FormField>
                    </VBox>
                    <VBox>
                        <p>{this.lbl('vehicle.details.price.restBPM.inputLbl')}</p>
                        <FormField>
                            <Input value={this.restBPM ?? ""} name="restBPM" onChange={this.onInputChange.bind(this)} />
                            {this.renderConfirmBar('restBPM')}
                            <FieldError errors={this.restBPMErrors} />
                            {this.props.bpmEstimateWork ? <Loader small /> :<BpmEstimate estimate={this.bpmEstimate} apply={this.applyBpmEstimate.bind(this)} />}
                        </FormField>
                    </VBox>
                    <VBox>
                        <p>{this.lbl('vehicle.details.price.b2bPrice.inputLbl')}</p>
                        <FormField>
                            <Input value={this.b2bPrice ?? ""} name="b2bPrice" onChange={this.onInputChange.bind(this)} />
                            {this.renderConfirmBar('b2bPrice')}
                            <FieldError errors={this.b2bPriceErrors} />
                        </FormField>
                    </VBox>
                    <VBox>
                        <p>{this.lbl('vehicle.details.price.b2cPrice.inputLbl')}</p>
                        <FormField>
                            <Input value={this.b2cPrice ?? ""} name="b2cPrice" onChange={this.onInputChange.bind(this)} />
                            {this.renderConfirmBar('b2cPrice')}
                            <FieldError errors={this.b2cPriceErrors} />
                        </FormField>
                    </VBox>
                </FormFields>
            </Container>
        );
    }
}

VehicleDetailsPriceComp.propTypes = {
    dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
    createStructuredSelector({
        propertyUpdateWork: selectWorks([REQUEST_VEHICLE_PROPERTY_UPDATE]),
        bpmEstimateWork: selectWorks([REQUEST_VEHICLE_BPM_ESTIMATE]),
    });

function mapDispatchToProps(dispatch, ownProps) {
    return {
        dispatch,
        setVehicleProperty(vehicleId, name, value, callback) {
            dispatch(setVehicleProperty(vehicleId, name, value, callback));
        },
        calculateBpmEstimate(vehicleId, callback) {
            dispatch(getBpmEstimate(vehicleId, callback));
        },
    };
}

const withConnect = connect(
    mapStateToProps,
    mapDispatchToProps,
);

export const VehicleDetailsPrice = compose(
    withConnect,
    withRouter,
)(VehicleDetailsPriceComp);
